// Cart Drawer JavaScript - Team Line Custom Theme
// REBUILT: Plus/Minus + Input Quantity Selector (No Dropdown)

(function() {
  'use strict';

  // ============================================================
  // SAFE JSON PARSER - Prevents "Unexpected end of JSON input" errors
  // ============================================================

  /**
   * Safely parse JSON from a fetch response.
   * - Reads response as text first to avoid JSON parse errors on empty/non-JSON responses.
   * - Returns parsed JSON, null (for empty), or an object with _nonJson flag for non-JSON content.
   * 
   * @param {Response} res - The fetch response object
   * @returns {Promise<Object|null>} Parsed JSON or error object
   */
  async function safeJson(res) {
    const text = await res.text();
    if (!text || text.trim() === '') {
      return null;
    }
    try {
      return JSON.parse(text);
    } catch (e) {
      // Non-JSON response (could be HTML error page from app, etc.)
      console.warn('[CART] Non-JSON response received:', text.substring(0, 200));
      return { _nonJson: true, _raw: text.substring(0, 500) };
    }
  }

  /**
   * Extract a meaningful error message from cart API response data.
   * Handles various response formats: standard Shopify errors, app errors, non-JSON.
   * 
   * @param {Object|null} data - Parsed response data
   * @param {number} status - HTTP status code
   * @returns {string} Human-readable error message
   */
  function extractErrorMessage(data, status) {
    if (!data) {
      return `Request failed with status ${status} (empty response)`;
    }
    if (data._nonJson) {
      return `Request failed with status ${status} (non-JSON response, see console)`;
    }
    // Standard Shopify error formats
    if (data.description) return data.description;
    if (data.message) return data.message;
    if (data.error) return typeof data.error === 'string' ? data.error : JSON.stringify(data.error);
    if (data.errors) {
      if (typeof data.errors === 'string') return data.errors;
      if (Array.isArray(data.errors)) return data.errors.join(', ');
      return JSON.stringify(data.errors);
    }
    return `Request failed with status ${status}`;
  }

  /**
   * Log detailed debug information when a cart request fails.
   * 
   * @param {string} endpoint - The API endpoint URL
   * @param {string} method - HTTP method
   * @param {number} status - HTTP status code
   * @param {Object|null} payload - Request payload (if any)
   * @param {Object|null} responseData - Parsed response data
   */
  function logCartRequestFailure(endpoint, method, status, payload, responseData) {
    console.group(`[CART] Request Failed: ${method} ${endpoint}`);
    console.log('HTTP Status:', status);
    if (payload) {
      console.log('Request Payload:', payload);
    }
    if (responseData) {
      if (responseData._nonJson) {
        console.log('Response (non-JSON):', responseData._raw);
      } else {
        console.log('Response Data:', responseData);
      }
    } else {
      console.log('Response: (empty)');
    }
    console.groupEnd();
  }

  // ============================================================
  // HELPER FUNCTIONS - Stock validation and image handling
  // ============================================================

  // Cache for variant fetch results (to avoid repeated 404s)
  const variantFetchCache = new Map();
  const FETCH_CACHE_TTL = 60000; // 1 minute cache
  const MAX_QTY_LIMIT = 999999;

  // ============================================================
  // BACKORDER DETECTION HELPER
  // ============================================================

  /**
   * Check if a cart item is a backorder line
   * Backorder items have _purchase_type: 'backorder' in properties
   */
  function isBackorderItem(item) {
    if (!item || !item.properties) return false;
    return item.properties['_purchase_type'] === 'backorder';
  }

  /**
   * Get backorder note from item properties
   */
  function getBackorderNote(item) {
    if (!item || !item.properties) return '';
    return item.properties['_backorder_note'] || 'Ships when stock arrives';
  }

  /**
   * Show a toast notification for stock/error messages
   * Much better UX than alert()
   */
  function showStockNotification(message, type = 'error') {
    
    // Remove any existing notification
    const existing = document.querySelector('.cart-notification');
    if (existing) existing.remove();

    // Create notification element
    const notification = document.createElement('div');
    notification.className = `cart-notification cart-notification--${type}`;
    notification.style.cssText = `
      position: fixed;
      top: 100px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 999999;
      max-width: 90vw;
      width: max-content;
    `;
    notification.innerHTML = `
      <div class="cart-notification__content" style="
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 16px 20px;
        background-color: #fff;
        border-radius: 12px;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15), 0 2px 10px rgba(0, 0, 0, 0.1);
        border: 1px solid #E2E8F0;
        border-left: 4px solid ${type === 'error' ? '#F56565' : '#00C896'};
      ">
        <svg style="flex-shrink: 0; color: ${type === 'error' ? '#F56565' : '#00C896'};" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          ${type === 'error' 
            ? '<circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line>'
            : '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline>'
          }
        </svg>
        <span style="font-size: 14px; font-weight: 500; color: #081F2C; line-height: 1.4;">${message}</span>
        <button type="button" style="
          flex-shrink: 0;
          padding: 4px;
          margin-left: 8px;
          border: none;
          background: none;
          cursor: pointer;
          color: #4A5A67;
          border-radius: 4px;
        " aria-label="Close" data-notification-close>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    `;

    // Add to page
    document.body.appendChild(notification);

    // Close button handler
    const closeBtn = notification.querySelector('[data-notification-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        notification.style.opacity = '0';
        setTimeout(() => notification.remove(), 300);
      });
    }

    // Animate in
    notification.style.opacity = '0';
    notification.style.transition = 'opacity 0.3s ease';
    setTimeout(() => {
      notification.style.opacity = '1';
    }, 10);

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.style.opacity = '0';
        setTimeout(() => notification.remove(), 300);
      }
    }, 5000);
  }

  /**
   * Check if a cached fetch result is still valid
   */
  function isCacheValid(cacheEntry) {
    if (!cacheEntry) return false;
    return (Date.now() - cacheEntry.timestamp) < FETCH_CACHE_TTL;
  }

  /**
   * Extract product handle from URL
   */
  function extractProductHandle(url) {
    if (!url) return null;
    const match = url.match(/\/products\/([^\/\?]+)/);
    return match ? match[1] : null;
  }

  /**
   * Get available stock for a cart item
   * Handles all Shopify API response formats
   */
  function getAvailableStock(item) {
    // Default to unlimited if no inventory management
    let maxQty = MAX_QTY_LIMIT;
    let hasInventoryLimit = false;

    // Check if variant has inventory management enabled
    if (item.variant) {
      const inventoryMgmt = item.variant.inventory_management;
      const inventoryPolicy = item.variant.inventory_policy;

      // Only limit if Shopify manages inventory AND policy is deny
      if (inventoryMgmt === 'shopify' && inventoryPolicy === 'deny') {
        const inventoryQty = item.variant.inventory_quantity;
        
        if (inventoryQty !== undefined && inventoryQty !== null) {
          maxQty = Math.max(0, parseInt(inventoryQty, 10));
          hasInventoryLimit = true;
        }
      }
    }

    // Ensure maxQty is at least current quantity (can't reduce below what's in cart)
    if (item.quantity && item.quantity > maxQty && hasInventoryLimit) {
      // If current quantity exceeds stock, use current quantity as minimum
      // This prevents issues when stock decreases after item is added
      maxQty = Math.max(maxQty, item.quantity);
    }

    return { maxQty, hasInventoryLimit };
  }

  /**
   * Limit quantity to available stock
   */
  function limitQuantityToStock(qty, stock) {
    const maxQty = Math.min(stock, MAX_QTY_LIMIT);
    return Math.max(1, Math.min(parseInt(qty, 10) || 1, maxQty));
  }

  /**
   * Unified image normalization function
   * Handles ALL Shopify image formats for product pages, cart, and AJAX responses
   */
  function normalizeImageUrl(itemOrMedia) {
    let imageSrc = '';

    // Handle different input types
    const item = itemOrMedia || {};
    const media = itemOrMedia;

    // Try variant featured_media first (variant-specific image)
    if (item.variant && item.variant.featured_media) {
      const variantMedia = item.variant.featured_media;
      if (typeof variantMedia === 'string') {
        imageSrc = variantMedia;
      } else if (variantMedia.src) {
        imageSrc = variantMedia.src;
      } else if (variantMedia.url) {
        imageSrc = variantMedia.url;
      } else if (variantMedia.preview && variantMedia.preview.image) {
        const preview = variantMedia.preview.image;
        if (typeof preview === 'string') {
          imageSrc = preview;
        } else if (preview.src) {
          imageSrc = preview.src;
        } else if (preview.url) {
          imageSrc = preview.url;
        }
      } else if (variantMedia.preview_image && variantMedia.preview_image.src) {
        imageSrc = variantMedia.preview_image.src;
      }
    }

    // Try item.image (string or object)
    if (!imageSrc && item.image) {
      if (typeof item.image === 'string') {
        imageSrc = item.image;
      } else if (item.image.src) {
        imageSrc = item.image.src;
      } else if (item.image.url) {
        imageSrc = item.image.url;
      }
    }

    // Try item.featured_image
    if (!imageSrc && item.featured_image) {
      if (typeof item.featured_image === 'string') {
        imageSrc = item.featured_image;
      } else if (item.featured_image.src) {
        imageSrc = item.featured_image.src;
      } else if (item.featured_image.url) {
        imageSrc = item.featured_image.url;
      }
    }

    // Try item.product.featured_image
    if (!imageSrc && item.product && item.product.featured_image) {
      const prodImg = item.product.featured_image;
      if (typeof prodImg === 'string') {
        imageSrc = prodImg;
      } else if (prodImg.src) {
        imageSrc = prodImg.src;
      } else if (prodImg.url) {
        imageSrc = prodImg.url;
      }
    }

    // Try media array (for product pages)
    if (!imageSrc && item.media && Array.isArray(item.media) && item.media.length > 0) {
      const firstMedia = item.media[0];
      if (firstMedia.src) {
        imageSrc = firstMedia.src;
      } else if (firstMedia.preview_image && firstMedia.preview_image.src) {
        imageSrc = firstMedia.preview_image.src;
      }
    }

    // Try direct media object (for product gallery)
    if (!imageSrc && media) {
      if (typeof media === 'string') {
        imageSrc = media;
      } else if (media.src) {
        imageSrc = media.src;
      } else if (media.url) {
        imageSrc = media.url;
      } else if (media.preview_image && media.preview_image.src) {
        imageSrc = media.preview_image.src;
      }
    }

    // Normalize URL format
    if (imageSrc) {
      // Protocol-relative URL (//cdn.shopify.com/...)
      if (imageSrc.startsWith('//')) {
        imageSrc = `https:${imageSrc}`;
      }
      // Relative URL (/products/...)
      else if (imageSrc.startsWith('/')) {
        imageSrc = `https://${window.location.hostname}${imageSrc}`;
      }
      // Already absolute URL - keep as is
    }

    return imageSrc;
  }

  /**
   * Get existing cart item for a variant
   * Returns the cart item object if it exists, null otherwise
   */
  async function getExistingCartItem(variantId) {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return null;
      
      const cart = await safeJson(response);
      if (!cart || !cart.items) return null;
      const existingItem = cart.items.find(item => item.variant_id === parseInt(variantId, 10));
      
      return existingItem || null;
    } catch (error) {
      // console.error('[CART] Error fetching cart:', error);
      return null;
    }
  }

  /**
   * Get existing cart quantity for a variant
   * Returns the current quantity of this variant in the cart
   */
  async function getExistingCartQuantity(variantId) {
    const existingItem = await getExistingCartItem(variantId);
    return existingItem ? existingItem.quantity : 0;
  }

  /**
   * Get available stock for a variant
   * Fetches variant data to get inventory quantity
   */
  async function getVariantStock(variantId, productId = null, productHandle = null) {
    try {
      
      // Try to get from product data if available
      if (window.product && window.product.variants) {
        const variant = window.product.variants.find(v => v.id === parseInt(variantId, 10));
        
        if (variant) {
          const inventoryMgmt = variant.inventory_management;
          const inventoryPolicy = variant.inventory_policy;
          const inventoryQty = variant.inventory_quantity;
          
          
          // If Shopify manages inventory and policy is deny (don't allow overselling)
          if (inventoryMgmt === 'shopify' && inventoryPolicy === 'deny') {
            if (inventoryQty !== undefined && inventoryQty !== null) {
              const stock = Math.max(0, parseInt(inventoryQty, 10));
              return stock;
            }
          }
        }
      }

      // Fallback: try to fetch variant data
      let variantUrl = null;
      if (productHandle) {
        variantUrl = `/products/${productHandle}/variants/${variantId}.json`;
      } else if (productId) {
        variantUrl = `/products/${productId}/variants/${variantId}.json`;
      } else {
        // Try to get product handle from current page
        const productHandleMatch = window.location.pathname.match(/\/products\/([^\/]+)/);
        if (productHandleMatch) {
          variantUrl = `/products/${productHandleMatch[1]}/variants/${variantId}.json`;
        }
      }

      if (variantUrl) {
        const variantResponse = await fetch(variantUrl);
        if (variantResponse.ok) {
          const variantData = await safeJson(variantResponse);
          if (variantData && variantData.variant) {
            const inventoryMgmt = variantData.variant.inventory_management;
            const inventoryPolicy = variantData.variant.inventory_policy;
            
            if (inventoryMgmt === 'shopify' && inventoryPolicy === 'deny') {
              const inventoryQty = variantData.variant.inventory_quantity;
              if (inventoryQty !== undefined && inventoryQty !== null) {
                return Math.max(0, parseInt(inventoryQty, 10));
              }
            }
          }
        }
      }

      // Default to unlimited
      return MAX_QTY_LIMIT;
    } catch (error) {
      // console.debug('[CART] Error fetching variant stock:', error);
      return MAX_QTY_LIMIT;
    }
  }

  /**
   * Calculate total quantity in cart (sum of all line item quantities)
   */
  function calculateTotalCartQuantity(cart) {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce((total, item) => total + (item.quantity || 0), 0);
  }

  /**
   * Build quantity selector UI (minus button + input + plus button)
   */
  function buildQuantityUI(itemKey, currentQty, maxQty) {
    const limitedQty = limitQuantityToStock(currentQty, maxQty);
    
    return `
      <div class="cart-item__quantity-wrapper">
        <div class="cart-item__quantity-controls" data-cart-item-quantity-controls data-key="${itemKey}" data-max-qty="${maxQty}">
          <button type="button" class="cart-item__quantity-btn cart-item__quantity-btn--minus" data-cart-item-quantity-minus aria-label="Decrease quantity">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
          <input 
            type="number" 
            class="cart-item__quantity-input" 
            data-cart-item-quantity-input
            value="${limitedQty}" 
            min="1" 
            max="${maxQty}"
            aria-label="Quantity"
          >
          <button type="button" class="cart-item__quantity-btn cart-item__quantity-btn--plus" data-cart-item-quantity-plus aria-label="Increase quantity">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
        </div>
        <div class="cart-item__stock-error" data-stock-error style="display: none;">
          No more stock available
        </div>
      </div>
    `;
  }

  /**
   * Attach event listeners to quantity controls
   */
  function attachQuantityListeners(controlsElement, cartDrawerInstance) {
    const itemKey = controlsElement.getAttribute('data-key');
    const maxQtyAttr = controlsElement.getAttribute('data-max-qty');
    const maxQty = maxQtyAttr ? parseInt(maxQtyAttr, 10) : MAX_QTY_LIMIT;
    const minusBtn = controlsElement.querySelector('[data-cart-item-quantity-minus]');
    const plusBtn = controlsElement.querySelector('[data-cart-item-quantity-plus]');
    const input = controlsElement.querySelector('[data-cart-item-quantity-input]');
    
    // Find the stock error element (sibling of controls)
    const wrapper = controlsElement.closest('.cart-item__quantity-wrapper');
    const stockError = wrapper ? wrapper.querySelector('[data-stock-error]') : null;
    

    if (!minusBtn || !plusBtn || !input || !itemKey) {
      return;
    }

    // Minus button - decrease by 1, minimum 1
    minusBtn.addEventListener('click', () => {
      const currentQty = parseInt(input.value) || 1;
      const newQty = Math.max(1, currentQty - 1);
      input.value = newQty;
      
      // Hide error when decreasing (now below max)
      if (stockError) {
        stockError.style.display = 'none';
      }
      
      cartDrawerInstance.updateQuantity(itemKey, newQty);
    });

    // Plus button - increase by 1
    plusBtn.addEventListener('click', async () => {
      const currentQty = parseInt(input.value) || 1;
      const newQty = currentQty + 1;
      
      // Hide any existing error before trying
      if (stockError) {
        stockError.style.display = 'none';
      }
      
      // Try to update - function handles showing error and reverting if needed
      await cartDrawerInstance.updateQuantityWithStockCheck(itemKey, newQty, stockError, input, currentQty);
    });

    // Store initial value
    input.setAttribute('data-last-valid-qty', input.value);
    
    // Input validation on blur - also checks stock via API
    input.addEventListener('blur', async () => {
      const typedValue = input.value.trim();
      const previousQty = parseInt(input.getAttribute('data-last-valid-qty')) || 1;
      let newQty;

      // If blank, invalid, or zero → reset to 1
      if (!typedValue || isNaN(typedValue) || parseInt(typedValue) <= 0) {
        newQty = 1;
      } else {
        newQty = parseInt(typedValue, 10);
      }

      // If quantity changed, update via API (which will check stock)
      if (newQty !== previousQty) {
        // Hide any existing error
        if (stockError) {
          stockError.style.display = 'none';
        }
        
        // Use the stock-checking update function
        const success = await cartDrawerInstance.updateQuantityWithStockCheck(
          itemKey, 
          newQty, 
          stockError, 
          input, 
          previousQty
        );
        
        // Update last valid qty based on what's now in the input
        input.setAttribute('data-last-valid-qty', input.value);
      }
    });

    // Input validation on Enter key
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        input.blur(); // Trigger blur validation
      }
    });
  }

  /**
   * Sync all quantity selectors after cart update
   * Re-checks stock and clamps quantities
   */
  function syncAllQuantitySelectorsAfterCartUpdate(cartDrawerInstance) {
    document.querySelectorAll('[data-cart-item-quantity-controls]').forEach(controls => {
      const cartItem = controls.closest('[data-cart-item]');
      const maxQty = parseInt(controls.getAttribute('data-max-qty')) || 
                     parseInt(cartItem?.getAttribute('data-available-stock')) || 
                     MAX_QTY_LIMIT;
      const input = controls.querySelector('[data-cart-item-quantity-input]');
      
      if (input) {
        const currentQty = parseInt(input.value) || 1;
        const limitedQty = limitQuantityToStock(currentQty, maxQty);
        
        // Update input value and max attribute
        input.value = limitedQty;
        input.setAttribute('max', maxQty);
        input.setAttribute('data-last-valid-qty', limitedQty);
        
        // Update controls max-qty attribute
        controls.setAttribute('data-max-qty', maxQty);
      }
    });
  }

  // ============================================================
  // CART DRAWER CLASS
  // ============================================================

  class CartDrawer {
    constructor() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.overlay = document.querySelector('[data-cart-drawer-overlay]');
      this.panel = document.querySelector('[data-cart-drawer-panel]');
      this.closeBtn = document.querySelector('[data-cart-drawer-close]');
      this.itemsContainer = document.querySelector('[data-cart-drawer-items]');
      this.footer = document.querySelector('[data-cart-footer]');
      this.loadingState = document.querySelector('[data-cart-loading]');
      this.subtotalElement = document.querySelector('[data-cart-subtotal]');
      this.cartCountElements = document.querySelectorAll('[data-cart-count]');
      this.isRefreshing = false;
      
      if (!this.drawer || !this.panel) {
        // console.error('[CART] Drawer elements not found');
        return;
      }

      this.init();
    }

    init() {
      // Open cart drawer when cart icon clicked
      document.querySelectorAll('[data-cart-trigger]').forEach(trigger => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          const cartDrawerElement = document.querySelector('cart-drawer');
          if (cartDrawerElement) {
            cartDrawerElement.open();
          } else {
            this.open();
          }
        });
      });

      // Close cart drawer
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
          this.close();
        }
      });

      // Continue shopping buttons
      document.querySelectorAll('[data-cart-drawer-close]').forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      // Promo code toggle
      const promoToggle = document.querySelector('[data-promo-toggle]');
      const promoForm = document.querySelector('[data-promo-form]');
      if (promoToggle && promoForm) {
        promoToggle.addEventListener('click', () => {
          const isVisible = promoForm.style.display !== 'none';
          promoForm.style.display = isVisible ? 'none' : 'block';
        });
      }

      // Cart item interactions
      this.initCartItemListeners();

      // Listen for custom add-to-cart events
      document.addEventListener('cart:item-added', (e) => {
        this.onItemAdded(e.detail);
      });
    }

    initCartItemListeners() {
      // Quantity controls (plus/minus/input)
      document.querySelectorAll('[data-cart-item-quantity-controls]').forEach(controls => {
        attachQuantityListeners(controls, this);
      });

      // Remove item buttons
      document.querySelectorAll('[data-cart-item-remove]').forEach(btn => {
        const newBtn = btn.cloneNode(true);
        btn.parentNode.replaceChild(newBtn, btn);
        
        newBtn.addEventListener('click', () => {
          const key = newBtn.getAttribute('data-key');
          if (key) {
            this.removeItem(key);
          }
        });
      });
    }

    open() {
      // Re-query elements if not found
      if (!this.drawer || !this.panel) {
        this.drawer = document.querySelector('[data-cart-drawer]');
        this.panel = document.querySelector('[data-cart-drawer-panel]');
      }
      
      if (!this.drawer || !this.panel) {
        // console.error('[CART] Drawer elements missing - cannot open');
        return;
      }

      // Also ensure cart-drawer custom element has active class
      const cartDrawerEl = document.querySelector('cart-drawer');
      if (cartDrawerEl) {
        cartDrawerEl.classList.add('active');
      }

      this.drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      
      this.panel.style.transition = 'none';
      this.panel.style.transform = 'translateX(0)';
      
      void this.panel.offsetHeight;

      requestAnimationFrame(() => {
        this.panel.style.transition = '';
      });
    }

    close() {
      this.drawer.classList.remove('is-open');
      document.body.style.overflow = '';
      this.panel.style.transform = 'translateX(100%)';
      
      setTimeout(() => {
        document.querySelectorAll('[data-cart-item-badge]').forEach(badge => {
          badge.style.display = 'none';
        });
      }, 300);
    }

    showLoading() {
      if (this.loadingState) {
        this.loadingState.style.display = 'flex';
      }
    }

    hideLoading() {
      if (this.loadingState) {
        this.loadingState.style.display = 'none';
      }
    }

    async updateQuantity(key, quantity) {
      this.showLoading();
      const endpoint = '/cart/change.js';
      const payload = { id: key, quantity: quantity };

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const data = await safeJson(response);

        if (!response.ok) {
          logCartRequestFailure(endpoint, 'POST', response.status, payload, data);
          throw new Error(extractErrorMessage(data, response.status));
        }

        const cart = data;
        await this.refreshCart();
        this.updateCartCount(cart);
        this.updateSubtotal(cart.total_price);
        
        document.dispatchEvent(new CustomEvent('cart:updated'));

      } catch (error) {
        // console.error('[CART] Error updating quantity:', error);
        showStockNotification(error.message || 'Failed to update quantity. Please try again.', 'error');
      } finally {
        this.hideLoading();
      }
    }

    /**
     * Update quantity with stock check - shows inline error if stock exceeded
     * Returns true if successful, false if failed due to stock limit
     */
    async updateQuantityWithStockCheck(key, quantity, stockErrorElement, inputElement, previousQty) {
      this.showLoading();
      const endpoint = '/cart/change.js';
      const payload = { id: key, quantity: quantity };

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const responseData = await safeJson(response);

        if (!response.ok) {
          // Update failed - show error and log details
          logCartRequestFailure(endpoint, 'POST', response.status, payload, responseData);
          
          if (stockErrorElement) {
            stockErrorElement.style.display = 'block';
          }
          
          // Fetch current cart to get the actual max quantity (what Shopify allowed)
          try {
            const cartResponse = await fetch('/cart.js');
            const cartData = await safeJson(cartResponse);
            const currentItem = cartData?.items?.find(item => item.key === key);
            
            if (currentItem && inputElement) {
              // Set input to the actual quantity in cart (which is the max allowed)
              inputElement.value = currentItem.quantity;
            } else if (inputElement) {
              inputElement.value = previousQty;
            }
          } catch (e) {
            // Fallback to previous qty if cart fetch fails
            if (inputElement) {
              inputElement.value = previousQty;
            }
          }
          
          this.hideLoading();
          return false;
        }

        // Check if Shopify silently capped the quantity
        const updatedItem = responseData?.items?.find(item => item.key === key);
        
        if (updatedItem && updatedItem.quantity < quantity) {
          
          if (stockErrorElement) {
            stockErrorElement.style.display = 'block';
          }
          if (inputElement) {
            inputElement.value = updatedItem.quantity;
          }
          
          this.updateCartCount(responseData);
          this.updateSubtotal(responseData.total_price);
          this.hideLoading();
          return false;
        }

        // Success - quantity was accepted!
        
        if (stockErrorElement) {
          stockErrorElement.style.display = 'none';
        }
        if (inputElement && updatedItem) {
          inputElement.value = updatedItem.quantity;
        }
        
        this.updateCartCount(responseData);
        this.updateSubtotal(responseData.total_price);
        
        // Refresh cart on full success
        await this.refreshCart();
        
        this.hideLoading();
        document.dispatchEvent(new CustomEvent('cart:updated'));
        
        return true;

      } catch (error) {
        console.error('[CART] Exception in updateQuantityWithStockCheck:', error);
        
        if (stockErrorElement) {
          stockErrorElement.style.display = 'block';
        }
        if (inputElement) {
          inputElement.value = previousQty;
        }
        
        this.hideLoading();
        return false;
      }
    }

    async removeItem(key) {
      this.showLoading();
      const endpoint = '/cart/change.js';
      const payload = { id: key, quantity: 0 };

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const cart = await safeJson(response);

        if (!response.ok) {
          logCartRequestFailure(endpoint, 'POST', response.status, payload, cart);
          throw new Error(extractErrorMessage(cart, response.status));
        }

        await this.refreshCart();
        this.updateCartCount(cart);
        this.updateSubtotal(cart.total_price);
        
        document.dispatchEvent(new CustomEvent('cart:updated'));

        if (cart.item_count === 0) {
          const cartDrawerElement = document.querySelector('cart-drawer');
          if (cartDrawerElement) {
            setTimeout(() => cartDrawerElement.close(), 500);
          } else {
            setTimeout(() => this.close(), 500);
          }
        }

      } catch (error) {
        // console.error('[CART] Error removing item:', error);
        showStockNotification(error.message || 'Failed to remove item. Please try again.', 'error');
      } finally {
        this.hideLoading();
      }
    }

    async refreshCart() {
      if (this.isRefreshing) {
        return;
      }
      
      this.isRefreshing = true;
      
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) throw new Error('Failed to fetch cart');
        
        const cart = await safeJson(response);
        if (!cart || !cart.items) throw new Error('Invalid cart response');
        
        // Fetch inventory data for all items
        const variantPromises = cart.items.map(async (item) => {
          const { maxQty, hasInventoryLimit } = getAvailableStock(item);
          
          // Only fetch variant data if not already available and we need it
          if (!hasInventoryLimit && item.variant_id) {
            const cacheKey = `variant-${item.variant_id}`;
            const cachedResult = variantFetchCache.get(cacheKey);
            
            // Return cached result if valid
            if (isCacheValid(cachedResult)) {
              if (cachedResult.data) {
                return { ...item, maxQty: cachedResult.data.maxQty, hasInventoryLimit: true };
              }
              // Cached 404/error - skip fetch
              return { ...item, maxQty, hasInventoryLimit };
            }
            
            try {
              // Extract product handle from item URL (most reliable)
              const productHandle = extractProductHandle(item.url) || item.handle;
              
              // Only attempt fetch if we have a handle
              if (productHandle) {
                const variantUrl = `/products/${productHandle}/variants/${item.variant_id}.json`;
                const variantResponse = await fetch(variantUrl);
                
                if (variantResponse.ok) {
                  const variantData = await safeJson(variantResponse);
                  if (variantData && variantData.variant) {
                    const inventoryMgmt = variantData.variant.inventory_management;
                    const inventoryPolicy = variantData.variant.inventory_policy;
                    
                    if (inventoryMgmt === 'shopify' && inventoryPolicy === 'deny') {
                      const inventoryQty = variantData.variant.inventory_quantity;
                      if (inventoryQty !== undefined && inventoryQty !== null) {
                        const stock = Math.max(0, parseInt(inventoryQty, 10));
                        const finalStock = Math.max(stock, item.quantity);
                        
                        // Cache the successful result
                        variantFetchCache.set(cacheKey, { 
                          timestamp: Date.now(), 
                          data: { maxQty: finalStock } 
                        });
                        
                        return { ...item, maxQty: finalStock, hasInventoryLimit: true };
                      }
                    }
                  }
                  // Cache successful but no inventory limit
                  variantFetchCache.set(cacheKey, { timestamp: Date.now(), data: null });
                } else {
                  // Cache the 404/error to prevent repeated fetches
                  variantFetchCache.set(cacheKey, { timestamp: Date.now(), data: null });
                }
              }
            } catch (err) {
              // Cache the error to prevent repeated fetches
              variantFetchCache.set(cacheKey, { timestamp: Date.now(), data: null });
            }
          }
          
          return { ...item, maxQty, hasInventoryLimit };
        });
        
        const itemsWithInventory = await Promise.all(variantPromises);
        
        // Rebuild cart items HTML
        if (cart.item_count === 0) {
          this.itemsContainer.innerHTML = `
            <div class="cart-drawer__empty" data-cart-empty>
              <div class="cart-drawer__empty-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <circle cx="9" cy="21" r="1"></circle>
                  <circle cx="20" cy="21" r="1"></circle>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                </svg>
              </div>
              <p class="cart-drawer__empty-text">Your cart is empty</p>
              <a href="/collections/all" class="btn btn--primary">Continue Shopping</a>
            </div>
          `;
          this.footer.style.display = 'none';
        } else {
          let itemsHTML = '';
          let hasBackorderItems = false;
          
          itemsWithInventory.forEach(item => {
            const maxQty = item.maxQty || MAX_QTY_LIMIT;
            const currentQty = item.quantity || 1;
            
            // BACKORDER: Check if this is a backorder item
            const itemIsBackorder = isBackorderItem(item);
            const backorderNote = itemIsBackorder ? getBackorderNote(item) : '';
            if (itemIsBackorder) hasBackorderItems = true;
            
            // For backorder items, don't limit quantity (it's an untracked variant)
            const effectiveMaxQty = itemIsBackorder ? MAX_QTY_LIMIT : maxQty;
            
            // Normalize image URL using helper function
            const imageSrc = normalizeImageUrl(item);
            
            const variantTitle = item.variant_title && !item.variant_title.includes('Default') 
              ? item.variant_title 
              : '';
            const finalPrice = this.formatMoney(item.final_line_price);
            const originalPrice = item.original_line_price !== item.final_line_price 
              ? this.formatMoney(item.original_line_price) 
              : '';
            
            // Build quantity UI (plus/minus + input)
            const qtyUI = buildQuantityUI(item.key, currentQty, effectiveMaxQty);
            
            itemsHTML += `
              <div class="cart-item ${itemIsBackorder ? 'cart-item--backorder' : ''}" data-cart-item data-key="${item.key}" data-available-stock="${effectiveMaxQty}" ${itemIsBackorder ? 'data-is-backorder="true"' : ''}>
                <div class="cart-item__badge" data-cart-item-badge style="display: none;">Just Added</div>
                ${itemIsBackorder ? '<div class="cart-item__backorder-badge">Backorder</div>' : ''}
                <div class="cart-item__content">
                  <a href="${item.url}" class="cart-item__image-wrapper">
                    ${imageSrc 
                      ? `<img src="${imageSrc}" alt="${item.title || item.product_title || 'Product'}" class="cart-item__image" loading="lazy" width="150" height="150">` 
                      : `<div class="cart-item__image cart-item__image--placeholder">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <circle cx="9" cy="21" r="1"></circle>
                            <circle cx="20" cy="21" r="1"></circle>
                            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                          </svg>
                        </div>`
                    }
                  </a>
                  <div class="cart-item__details">
                    <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
                    ${variantTitle ? `<div class="cart-item__variant">${variantTitle}</div>` : ''}
                    ${itemIsBackorder && backorderNote ? `<div class="cart-item__backorder-note">${backorderNote}</div>` : ''}
                    <div class="cart-item__meta">
                      <div class="cart-item__quantity-select-wrapper">
                        ${qtyUI}
                      </div>
                      <div class="cart-item__price">
                        ${originalPrice ? `<span class="cart-item__price--sale">${finalPrice}</span><span class="cart-item__price--compare">${originalPrice}</span>` : finalPrice}
                      </div>
                    </div>
                  </div>
                  <button type="button" class="cart-item__remove" data-cart-item-remove data-key="${item.key}">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>
            `;
          });
          
          // BACKORDER: Add cart-level banner if there are backorder items
          if (hasBackorderItems) {
            itemsHTML = `
              <div class="cart-drawer__backorder-banner" data-backorder-banner>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <span>Your order contains backordered items. Backorders ship when stock arrives.</span>
              </div>
            ` + itemsHTML;
          }
          
          this.itemsContainer.innerHTML = itemsHTML;
          this.footer.style.display = 'block';
          this.updateSubtotal(cart.total_price);
        }

        // Re-init listeners for new elements
        this.initCartItemListeners();
        
        // Sync all quantity selectors
        syncAllQuantitySelectorsAfterCartUpdate(this);

      } catch (error) {
        // console.error('[CART] Error refreshing cart:', error);
      } finally {
        this.isRefreshing = false;
      }
    }

    /**
     * Update cart count badge on all elements
     * Uses Dawn's approach: set content to number or empty string
     * CSS :empty hides when empty
     */
    updateCartCount(cart) {
      // Calculate total quantity (sum of all line item quantities)
      const totalQty = calculateTotalCartQuantity(cart);
      
      // Update all cart count elements (including header badge on mobile & desktop)
      const allCartCountElements = document.querySelectorAll('[data-cart-count]');
      allCartCountElements.forEach(element => {
        // Update text content: number if > 0, empty string if 0
        // CSS :empty will hide when textContent is ''
        element.textContent = totalQty > 0 ? totalQty : '';
      });
      
      // Update aria-label on cart button for accessibility
      const cartButton = document.querySelector('[data-cart-trigger]');
      if (cartButton) {
        cartButton.setAttribute('aria-label', `Cart (${totalQty} items)`);
      }
    }

    updateSubtotal(totalPrice) {
      if (this.subtotalElement) {
        this.subtotalElement.textContent = this.formatMoney(totalPrice);
      }
    }

    formatMoney(cents) {
      if (typeof cents === 'string') {
        cents = parseInt(cents);
      }
      const dollars = (cents / 100).toFixed(2);
      return `$${dollars}`;
    }

    onItemAdded(item) {
      // Only open if not already open
      if (!this.drawer.classList.contains('is-open')) {
        this.open();
      }

      // Show "Just Added" badge
      setTimeout(() => {
        const itemElement = document.querySelector(`[data-cart-item][data-key="${item.key}"]`);
        if (itemElement) {
          const badge = itemElement.querySelector('[data-cart-item-badge]');
          if (badge) {
            badge.style.display = 'block';
            setTimeout(() => {
              badge.style.display = 'none';
            }, 3000);
          }
        }
      }, 400);
    }
  }

  // ============================================================
  // ADD TO CART HANDLER
  // ============================================================

  class AddToCartHandler {
    constructor() {
      this.init();
    }

    init() {
      // Handle add to cart forms
      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (form.matches('[data-product-form]') || form.matches('form[action*="/cart/add"]')) {
          e.preventDefault();
          this.addToCart(form);
        }
      });

      // Handle Quick Add buttons
      document.body.addEventListener('click', (e) => {
        const quickAddBtn = e.target.closest('[data-quick-add]');
        if (quickAddBtn) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.quickAdd(quickAddBtn);
        }
      }, false);
    }

    async addToCart(form) {
      const submitBtn = form.querySelector('[type="submit"]');

      try {
        if (submitBtn) {
          submitBtn.classList.add('is-loading');
          submitBtn.disabled = true;
        }

        // Get variant ID and requested quantity from form
        const formData = new FormData(form);
        const variantId = formData.get('id');
        const requestedQty = parseInt(formData.get('quantity') || '1', 10);

        if (!variantId) {
          throw new Error('Variant ID is required');
        }

        // CRITICAL: Check existing cart item and available stock BEFORE any request
        const existingItem = await getExistingCartItem(variantId);
        const existingQty = existingItem ? existingItem.quantity : 0;
        const availableStock = await getVariantStock(variantId);
        
        // Calculate total quantity if we add the requested amount
        const totalQty = existingQty + requestedQty;
        
        // Determine final quantity (clamp to stock)
        let finalQty = requestedQty;
        
        if (availableStock < MAX_QTY_LIMIT && totalQty > availableStock) {
          // Clamp to available stock
          finalQty = Math.max(0, availableStock - existingQty);
          
          if (finalQty <= 0) {
            showStockNotification(`Sorry, only ${availableStock} ${availableStock === 1 ? 'item is' : 'items are'} available and you already have ${existingQty} in your cart.`, 'error');
            if (submitBtn) {
              submitBtn.classList.remove('is-loading');
              submitBtn.disabled = false;
            }
            return;
          }
          
          if (finalQty < requestedQty) {
            showStockNotification(`Only ${finalQty} more ${finalQty === 1 ? 'item' : 'items'} can be added. Stock limit: ${availableStock}.`, 'error');
          }
        }
        
        // If item already exists, use UPDATE instead of ADD
        if (existingItem) {
          // Calculate new total quantity
          const newTotalQty = existingQty + finalQty;
          // Clamp to stock
          const clampedTotalQty = availableStock < MAX_QTY_LIMIT ? Math.min(newTotalQty, availableStock) : newTotalQty;
          
          // Use cart/change.js to update existing item
          const endpoint = '/cart/change.js';
          const payload = { id: existingItem.key, quantity: clampedTotalQty };
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });

          const data = await safeJson(response);
          if (!response.ok) {
            logCartRequestFailure(endpoint, 'POST', response.status, payload, data);
            throw new Error(extractErrorMessage(data, response.status));
          }
        } else {
          // New item - use cart/add.js with clamped quantity
          formData.set('quantity', finalQty.toString());
          
          const endpoint = '/cart/add.js';
          const response = await fetch(endpoint, {
            method: 'POST',
            body: formData
          });

          const data = await safeJson(response);
          if (!response.ok) {
            // Convert FormData to object for logging
            const payloadObj = {};
            formData.forEach((value, key) => { payloadObj[key] = value; });
            logCartRequestFailure(endpoint, 'POST', response.status, payloadObj, data);
            throw new Error(extractErrorMessage(data, response.status));
          }
        }

        // Refresh cart and update UI
        const cartResponse = await fetch('/cart.js');
        const cart = await safeJson(cartResponse);

        // Update cart count (supports full cart page flow - no drawer)
        if (cart && window.cartDrawerInstance && typeof window.cartDrawerInstance.updateCartCount === 'function') {
          window.cartDrawerInstance.updateCartCount(cart);
        } else if (cart) {
          const cartCountElements = document.querySelectorAll('[data-cart-count]');
          const totalQty = calculateTotalCartQuantity(cart);
          cartCountElements.forEach(el => {
            el.textContent = totalQty > 0 ? totalQty : '';
          });
        }

        document.dispatchEvent(new CustomEvent('cart:updated'));

        // B2B-friendly behavior: send customers to the full cart page (no cart drawer)
        const cartUrl = (window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart';
        window.location.href = cartUrl;

      } catch (error) {
        // console.error('[CART] Error adding to cart:', error);
        showStockNotification(error.message || 'Failed to add item to cart. Please try again.', 'error');
      } finally {
        if (submitBtn) {
          submitBtn.classList.remove('is-loading');
          submitBtn.disabled = false;
        }
      }
    }

    async quickAdd(button) {
      if (button.disabled || button.classList.contains('is-loading')) {
        return;
      }
      
      const productId = button.getAttribute('data-product-id');
      if (!productId) {
        // console.error('[CART] No product ID found');
        return;
      }

      // Get variant ID (product ID in quick add is usually the variant ID)
      const variantId = productId;
      const requestedQty = 1;

      // CRITICAL: Check existing cart item and available stock BEFORE any request
      const existingItem = await getExistingCartItem(variantId);
      const existingQty = existingItem ? existingItem.quantity : 0;
      
      const inventoryQty = parseInt(button.getAttribute('data-variant-inventory')) || MAX_QTY_LIMIT;
      const inventoryMgmt = button.getAttribute('data-variant-inventory-management');
      const inventoryPolicy = button.getAttribute('data-variant-inventory-policy');
      
      // Check if out of stock
      if (inventoryMgmt === 'shopify' && inventoryPolicy === 'deny' && inventoryQty <= 0) {
        showStockNotification('Sorry, this item is currently out of stock.', 'error');
        return;
      }

      // Get available stock
      const availableStock = inventoryMgmt === 'shopify' && inventoryPolicy === 'deny' 
        ? inventoryQty 
        : MAX_QTY_LIMIT;
      
      // Calculate total quantity
      const totalQty = existingQty + requestedQty;
      
      // Determine final quantity (clamp to stock)
      let finalQty = requestedQty;
      
      if (availableStock < MAX_QTY_LIMIT && totalQty > availableStock) {
        finalQty = Math.max(0, availableStock - existingQty);
        
        if (finalQty <= 0) {
          showStockNotification(`Sorry, only ${availableStock} ${availableStock === 1 ? 'item is' : 'items are'} available and you already have ${existingQty} in your cart.`, 'error');
          return;
        }
      }

      const originalText = button.textContent.trim();
      button.classList.add('is-loading');
      button.disabled = true;
      button.textContent = 'Adding...';

      try {
        // If item already exists, use UPDATE instead of ADD
        if (existingItem) {
          const newTotalQty = existingQty + finalQty;
          const clampedTotalQty = availableStock < MAX_QTY_LIMIT ? Math.min(newTotalQty, availableStock) : newTotalQty;
          
          const endpoint = '/cart/change.js';
          const payload = { id: existingItem.key, quantity: clampedTotalQty };
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });

          const data = await safeJson(response);
          if (!response.ok) {
            logCartRequestFailure(endpoint, 'POST', response.status, payload, data);
            throw new Error(extractErrorMessage(data, response.status));
          }
        } else {
          // New item - use cart/add.js
          const endpoint = '/cart/add.js';
          const payload = { id: productId, quantity: finalQty };
          const addResponse = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });

          const data = await safeJson(addResponse);
          if (!addResponse.ok) {
            logCartRequestFailure(endpoint, 'POST', addResponse.status, payload, data);
            throw new Error(extractErrorMessage(data, addResponse.status));
          }
        }

        const cartResponse = await fetch('/cart.js');
        const cart = await safeJson(cartResponse);

        // Update cart count immediately
        if (window.cartDrawerInstance) {
          window.cartDrawerInstance.updateCartCount(cart);
        } else {
          const cartCountElements = document.querySelectorAll('[data-cart-count]');
          const totalQty = calculateTotalCartQuantity(cart);
          cartCountElements.forEach(el => {
            el.textContent = totalQty > 0 ? totalQty : '';
          });
        }

        // Open cart drawer - direct DOM manipulation for reliability
        document.dispatchEvent(new CustomEvent('cart:updated'));

        button.textContent = 'Added!';
        const cartUrl = (window.routes && window.routes.cart_url) ? window.routes.cart_url : '/cart';
        setTimeout(() => {
          window.location.href = cartUrl;
        }, 250);

      } catch (error) {
        // console.error('[CART] Quick Add error:', error);
        showStockNotification(error.message || 'Failed to add item. Please try again.', 'error');
      } finally {
        // ALWAYS reset button state to prevent freeze
        setTimeout(() => {
          if (button.classList.contains('is-loading')) {
            button.textContent = originalText || 'Add to Cart';
            button.classList.remove('is-loading');
            button.disabled = false;
          }
        }, 2000);
      }
    }
    
    /**
     * Open cart drawer - direct DOM manipulation for maximum reliability
     * This bypasses custom element methods to ensure the drawer opens
     */
    openCartDrawer() {
      try {
        const cartDrawerEl = document.querySelector('cart-drawer');
        const drawerInner = document.querySelector('[data-cart-drawer]');
        const panel = document.querySelector('[data-cart-drawer-panel]');
        
        if (!cartDrawerEl) {
          // console.error('[CART] cart-drawer element not found');
          return;
        }
        
        // Add active class to custom element (triggers CSS visibility)
        cartDrawerEl.classList.add('active');
        
        // Also add is-open to inner drawer (for additional CSS rules)
        if (drawerInner) {
          drawerInner.classList.add('is-open');
        }
        
        // Force panel to slide in
        if (panel) {
          panel.style.transform = 'translateX(0)';
        }
        
        // Lock body scroll
        document.body.style.overflow = 'hidden';
        
      } catch (error) {
        // console.error('[CART] Error opening cart drawer:', error);
        // Reset body overflow if something went wrong
        document.body.style.overflow = '';
      }
    }
  }

  // ============================================================
  // INITIALIZATION
  // ============================================================

  async function initializeCartSystem() {
    if (window.cartDrawerInstance || window.addToCartHandlerInstance) {
      return;
    }

    window.cartDrawerInstance = new CartDrawer();
    window.addToCartHandlerInstance = new AddToCartHandler();
    
    // Initialize cart count badge on page load
    try {
      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) {
        const cart = await safeJson(cartResponse);
        if (cart) window.cartDrawerInstance.updateCartCount(cart);
      }
    } catch (error) {
      // console.debug('[CART] Error initializing cart count:', error);
    }
    
    // Listen for cart updates to refresh badge
    document.addEventListener('cart:updated', async () => {
      try {
        const cartResponse = await fetch('/cart.js');
        if (cartResponse.ok) {
          const cart = await safeJson(cartResponse);
          if (cart && window.cartDrawerInstance) {
            window.cartDrawerInstance.updateCartCount(cart);
          }
        }
      } catch (error) {
        // console.debug('[CART] Error updating cart count:', error);
      }
    });
    
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCartSystem);
  } else {
    initializeCartSystem();
  }

})();
